<template>
  <div class="login-outer">
    <div class="login-inner">
      <h4>{{ $t("changepwd.TITLE") }}</h4>
      <form @submit.prevent="handleSubmit">
        <label for="name">{{ $t("changepwd.FIELD_OLD_PWD") }}</label>
        <div>
          <input
            class="loginField"
            id="old_pwd"
            type="password"
            v-model="formData.old_pwd"
            v-on:change="validate"
            required
            autofocus
            autocomplete
            autoCorrect="off"
            autoCapitalize="none"
          />
        </div>
        <div>
          <label for="password">{{ $t("changepwd.FIELD_NEW_PWD") }}</label>
          <div>
            <input
              class="loginField"
              id="new_pwd"
              type="password"
              v-model="formData.new_pwd"
              v-on:change="validate"
              required
              autocomplete
              autoCorrect="off"
              autoCapitalize="none"
            />
          </div>
        </div>
        <div>
          <label for="password">{{ $t("changepwd.FIELD_REPEAT_PWD") }}</label>
          <div>
            <input
              class="loginField"
              id="rpt_pwd"
              type="password"
              v-model="formData.rpt_pwd"
              v-on:change="validate"
              required
              autocomplete
              autoCorrect="off"
              autoCapitalize="none"
            />
          </div>
        </div>
        <div>
          <p></p>
          <p>
            <button type="submit">
              {{ $t("changepwd.BUTTON_CHANGE") }}
            </button>
            &nbsp;
            <button @click="goback">
              {{ $t("changepwd.BUTTON_DISMISS") }}
            </button>
          </p>
        </div>
        <div>
          <p v-if="errors.length">
            <b>{{ $t("changepwd.CHANGE_ERRORS_TEXT") }}</b>
          </p>

          <ul class="errors">
            <li v-for="error in errors" :key="error">
              {{ error }}
            </li>
          </ul>
        </div>
        <div v-if="errorMsg">
          {{ errorMsg }}
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import validate from "validate.js";
import apiClient from "../services/apiClient";

validate.validators.custompwd = function (value, options) {
  const errors = [];
  if (options.alphabetic && options.alphabetic.minimum) {
    const n = (value.match(/[a-zA-Z]/g) || []).length;
    if (n < options.alphabetic.minimum) {
      const msg =
        options.alphabetic.message ||
        "must contain least %{count} alphabetic characters";
      errors.push(
        validate.format(msg, {
          count: options.alphabetic.minimum
        })
      );
    }
  }
  if (options.numeric && options.numeric.minimum) {
    const n = (value.match(/[0-9]/g) || []).length;
    if (n < options.numeric.minimum) {
      const msg =
        options.numeric.message ||
        "must contain least %{count} numeric characters";
      errors.push(
        validate.format(msg, {
          count: options.numeric.minimum
        })
      );
    }
  }
  if (options.specials && options.specials.chars && options.specials.minimum) {
    let specialRe = "";
    Object.values(options.specials.chars).forEach(c => {
      specialRe += "\\" + c;
    });
    const n = (value.match(new RegExp(`[${specialRe}]`, "g")) || []).length;
    if (n < options.specials.minimum) {
      const msg =
        options.specials.message ||
        "must contain least %{count} characters in the following set %{chars}";
      errors.push(
        validate.format(msg, {
          count: options.specials.minimum,
          chars: options.specials.chars
        })
      );
    }
  }
  if (options.mixCase && options.mixCase.enabled) {
    const lc = (value.match(/[a-z]/g) || []).length;
    const uc = (value.match(/[A-Z]/g) || []).length;
    if (lc === 0 || uc === 0) {
      errors.push(
        options.mixCase.message ||
          "must contain at least one uppercase and one lowercase characters"
      );
    }
  }
  return errors;
};

export default {
  data() {
    return {
      formData: {
        old_pwd: "",
        new_pwd: "",
        rpt_pwd: ""
      },
      errors: [],
      errorMsg: undefined,
      messages: {
        ERR_WRONG_USER_OR_LOGIN: this.$t("login.ERR_WRONG_USER_OR_LOGIN"),
        ERR_REMOTE_UNREACHABLE: this.$t("login.ERR_REMOTE_UNREACHABLE"),
        ERR_REMOTE_ERROR: this.$t("login.ERR_REMOTE_ERROR"),
        MSG_PASSWORD_CHANGED: this.$t("changepwd.MSG_PASSWORD_CHANGED")
      },
      serverMessages: {
        "Network Error": this.$t("login.ERR_LOCAL_UNREACHABLE"),
        "new password does not meet password policy rules": this.$t(
          "changepwd.ERR_SERVER_PWD_POLICY_RULES"
        ),
        "old password do not match": this.$t(
          "changepwd.ERR_SERVER_OLD_PWD_NOMATCH"
        )
      },
      passwordRules: {}
    };
  },
  mounted: function () {
    const client = this.createApiClient();
    client.getPasswordPolicies().then(data => {
      this.passwordRules = Object.assign({}, data.rules);
      this.validate();
    });
  },
  methods: {
    ...mapActions(["setUser", "setToken", "setProfile", "hasToken"]),
    ...mapGetters(["user"]),

    validate() {
      const passwordRules = this.passwordRules || {};
      const new_pwd_rules = {
        type: "string",
        presence: {
          allowEmpty: false,
          message: this.$t("changepwd.ERR_NEW_PWD_CANT_BE_BLANK")
        }
      };
      if (passwordRules.minLen) {
        new_pwd_rules.length = {
          minimum: passwordRules.minLen,
          tooShort: this.$t("changepwd.ERR_NEW_PWD_TOO_SHORT")
        };
      }
      if (
        passwordRules.minAlp ||
        passwordRules.minNum ||
        passwordRules.minSPE ||
        passwordRules.mixCase
      ) {
        const custom = {};
        if (passwordRules.minAlp) {
          custom.alphabetic = {
            minimum: passwordRules.minAlp,
            message: this.$t("changepwd.ERR_NEW_PWD_ALPHABETIC_TOO_SHORT")
          };
        }
        if (passwordRules.minNum) {
          custom.numeric = {
            minimum: passwordRules.minNum,
            message: this.$t("changepwd.ERR_NEW_PWD_NUMERIC_TOO_SHORT")
          };
        }
        if (passwordRules.minSPE) {
          custom.specials = {
            minimum: passwordRules.minSPE,
            chars: passwordRules.specialChars,
            message: this.$t("changepwd.ERR_NEW_PWD_SPECIALS_TOO_SHORT")
          };
        }
        if (passwordRules.mixCase) {
          custom.mixcased = {
            enabled: true,
            message: this.$t("changepwd.ERR_NEW_PWD_MIX_CASE")
          };
        }
        new_pwd_rules.custompwd = custom;
      }
      const constraints = {
        old_pwd: {
          type: "string",
          presence: {
            allowEmpty: false,
            message: this.$t("changepwd.ERR_OLD_PWD_CANT_BE_BLANK")
          }
        },
        new_pwd: new_pwd_rules,
        rpt_pwd: {
          type: "string",
          equality: {
            attribute: "new_pwd",
            message: this.$t("changepwd.ERR_RPT_PWD_MISMATCH")
          }
        }
      };
      const result = validate(this.formData, constraints);
      this.errors.length = 0;
      if (result) {
        Object.keys(result).forEach(k => {
          const key = validate.capitalize(validate.prettify(k)) + " ";
          const messages = result[k];
          messages.forEach(msg => {
            if (msg.startsWith(key)) {
              msg = msg.substring(key.length);
            }
            this.errors.push(msg);
          });
        });
      }
    },

    createApiClient() {
      return new apiClient({
        $http: this.$http,
        jwt: this.$store.getters.token
      });
    },

    handleSubmit(e) {
      e.preventDefault();
      this.errorMsg = undefined;
      this.validate();
      if (this.errors.length === 0) {
        const client = this.createApiClient();
        return client
          .changePassword(this.formData.old_pwd, this.formData.new_pwd)
          .then(response => {
            console.log("got ", response);
            window.alert(this.messages.MSG_PASSWORD_CHANGED);
            if (this.$route.params.nextUrl != null) {
              this.$router.push(this.$route.params.nextUrl);
            } else {
              this.$router.push("/");
            }
          })
          .catch(error => {
            const response = error.response || error;
            const data = response.data || response;
            const remoteError = data.error;
            console.log("change pwd failed", response);
            if (response.status === 401) {
              this.errorMsg = this.messages.ERR_WRONG_USER_OR_LOGIN;
            } else if (
              response.status === 500 &&
              remoteError &&
              remoteError.errno === "ECONNREFUSED"
            ) {
              this.errorMsg = this.messages.ERR_REMOTE_UNREACHABLE;
              console.log(this.errorMsg);
              //this.$emit("offline");
            } else {
              if (data.message) {
                const errorMsg = this.serverMessages[data.message];
                if (errorMsg) {
                  this.errorMsg = errorMsg;
                } else {
                  this.errorMsg = `${this.messages.ERR_REMOTE_ERROR} ${data.message}`;
                }
              } else {
                this.errorMsg = JSON.stringify(error);
              }
            }
          });
      }
    },

    goback() {
      this.$router.push("/");
    }
  }
};
</script>

<style scoped>
div.login-outer {
  width: 100%;
}
div.login-inner {
  width: 50%;
  margin: 0 auto;
}
div.login-inner h4 {
  text-align: center;
}
input.loginField {
  width: 80%;
}
ul.errors {
  list-style-position: inside;
}
</style>
